/**
 * Attribution Events Service
 * 
 * Tracks and queries detailed attribution funnel events from click to conversion.
 */

import { supabase } from "@/integrations/supabase/client";
import { withServiceErrorHandling } from "@/lib/service-error";
import { logger } from "@/lib/structured-logger";

import { Json } from '@/integrations/supabase/types';

export type EventType = 'click' | 'trial_signup' | 'demo_request' | 'conversion' | 'expansion';

export interface AttributionEvent {
  id: string;
  organization_id: string;
  post_id: string | null;
  short_link_id: string | null;
  event_type: EventType;
  event_date: string;
  contact_email: string | null;
  mrr_value: number;
  metadata: Json;
  created_at: string;
}

export interface FunnelMetrics {
  clicks: number;
  trialSignups: number;
  demoRequests: number;
  conversions: number;
  expansions: number;
  totalMRR: number;
  clickToTrialRate: number;
  trialToConversionRate: number;
  overallConversionRate: number;
}

export interface PostROI {
  postId: string;
  clicks: number;
  trials: number;
  conversions: number;
  mrr: number;
  roi: number;
  conversionRate: number;
}

export class AttributionEventsService {
  private static readonly logger = logger.child({ service: 'AttributionEventsService' });

  /**
   * Track attribution event
   */
  static async trackEvent(event: Omit<AttributionEvent, 'id' | 'created_at'>): Promise<AttributionEvent> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Tracking attribution event', { 
        type: event.event_type, 
        postId: event.post_id 
      });

      const { data, error } = await supabase
        .from('attribution_events')
        .insert(event)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('No data returned from insert');

      this.logger.info('Attribution event tracked', { eventId: data.id });

      return data as AttributionEvent;
    }, { operation: 'trackEvent', table: 'attribution_events' });
  }

  /**
   * Get funnel metrics for organization within date range
   */
  static async getEventFunnel(
    organizationId: string,
    startDate?: string,
    endDate?: string
  ): Promise<FunnelMetrics> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching funnel metrics', { organizationId, startDate, endDate });

      let query = supabase
        .from('attribution_events')
        .select('event_type, mrr_value')
        .eq('organization_id', organizationId);

      if (startDate) {
        query = query.gte('event_date', startDate);
      }
      if (endDate) {
        query = query.lte('event_date', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      const events = data || [];

      const clicks = events.filter(e => e.event_type === 'click').length;
      const trialSignups = events.filter(e => e.event_type === 'trial_signup').length;
      const demoRequests = events.filter(e => e.event_type === 'demo_request').length;
      const conversions = events.filter(e => e.event_type === 'conversion').length;
      const expansions = events.filter(e => e.event_type === 'expansion').length;
      const totalMRR = events
        .filter(e => e.event_type === 'conversion' || e.event_type === 'expansion')
        .reduce((sum, e) => sum + (e.mrr_value || 0), 0);

      const clickToTrialRate = clicks > 0 ? (trialSignups / clicks) * 100 : 0;
      const trialToConversionRate = trialSignups > 0 ? (conversions / trialSignups) * 100 : 0;
      const overallConversionRate = clicks > 0 ? (conversions / clicks) * 100 : 0;

      const metrics = {
        clicks,
        trialSignups,
        demoRequests,
        conversions,
        expansions,
        totalMRR,
        clickToTrialRate,
        trialToConversionRate,
        overallConversionRate,
      };

      this.logger.info('Funnel metrics calculated', { organizationId, ...metrics });

      return metrics;
    }, { operation: 'getEventFunnel', table: 'attribution_events' });
  }

  /**
   * Calculate ROI for specific post
   */
  static async getPostROI(postId: string): Promise<PostROI> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Calculating post ROI', { postId });

      const { data, error } = await supabase
        .from('attribution_events')
        .select('event_type, mrr_value')
        .eq('post_id', postId);

      if (error) throw error;

      const events = data || [];

      const clicks = events.filter(e => e.event_type === 'click').length;
      const trials = events.filter(e => e.event_type === 'trial_signup').length;
      const conversions = events.filter(e => e.event_type === 'conversion').length;
      const mrr = events
        .filter(e => e.event_type === 'conversion' || e.event_type === 'expansion')
        .reduce((sum, e) => sum + (e.mrr_value || 0), 0);

      const conversionRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
      const roi = mrr; // Simplified ROI - could factor in costs

      const postROI = {
        postId,
        clicks,
        trials,
        conversions,
        mrr,
        roi,
        conversionRate,
      };

      this.logger.info('Post ROI calculated', postROI);

      return postROI;
    }, { operation: 'getPostROI', table: 'attribution_events' });
  }

  /**
   * Get all attribution events for organization
   */
  static async getByOrganization(organizationId: string): Promise<AttributionEvent[]> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching attribution events for organization', { organizationId });

      const { data, error } = await supabase
        .from('attribution_events')
        .select('*')
        .eq('organization_id', organizationId)
        .order('event_date', { ascending: false });

      if (error) throw error;

      return (data || []) as AttributionEvent[];
    }, { operation: 'getByOrganization', table: 'attribution_events' });
  }

  /**
   * Calculate ROI for campaign
   */
  static async getCampaignROI(campaignId: string): Promise<PostROI> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Calculating campaign ROI', { campaignId });

      // Get all posts in campaign
      const { data: campaignPosts, error: postsError } = await supabase
        .from('campaign_posts')
        .select('post_id')
        .eq('campaign_id', campaignId);

      if (postsError) throw postsError;

      const postIds = campaignPosts?.map(cp => cp.post_id) || [];

      if (postIds.length === 0) {
        return {
          postId: campaignId,
          clicks: 0,
          trials: 0,
          conversions: 0,
          mrr: 0,
          roi: 0,
          conversionRate: 0,
        };
      }

      const { data, error } = await supabase
        .from('attribution_events')
        .select('event_type, mrr_value')
        .in('post_id', postIds);

      if (error) throw error;

      const events = data || [];

      const clicks = events.filter(e => e.event_type === 'click').length;
      const trials = events.filter(e => e.event_type === 'trial_signup').length;
      const conversions = events.filter(e => e.event_type === 'conversion').length;
      const mrr = events
        .filter(e => e.event_type === 'conversion' || e.event_type === 'expansion')
        .reduce((sum, e) => sum + (e.mrr_value || 0), 0);

      const conversionRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
      const roi = mrr;

      const campaignROI = {
        postId: campaignId,
        clicks,
        trials,
        conversions,
        mrr,
        roi,
        conversionRate,
      };

      this.logger.info('Campaign ROI calculated', campaignROI);

      return campaignROI;
    }, { operation: 'getCampaignROI', table: 'attribution_events' });
  }
}
